import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { LogOut, Settings, ChevronDown } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "@/hooks/useTranslation";

export function UserDropdown() {
  const { user, signOut } = useAuth();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = async () => {
    setOpen(false);
    await signOut();
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 hover:bg-neutral-50 rounded-lg px-2 py-1 transition-colors"
      >
        <div className="text-right">
          <p className="text-xs font-medium text-neutral-800 leading-none">
            {user?.displayName || "User"}
          </p>
          <p className="text-xs text-neutral-400 mt-0.5 leading-none">
            {user?.email}
          </p>
        </div>
        {user?.photoUrl ? (
          <img src={user.photoUrl} alt="" className="w-7 h-7 rounded-full" />
        ) : (
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center">
            <span className="text-white text-xs font-bold">
              {(user?.displayName || "U").charAt(0).toUpperCase()}
            </span>
          </div>
        )}
        <ChevronDown className={`w-3 h-3 text-neutral-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-neutral-200 rounded-lg shadow-lg py-1 z-50">
          <Link
            to="/settings"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2.5 px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
          >
            <Settings className="w-4 h-4 text-neutral-400" />
            {t("userMenu.settings")}
          </Link>
          <div className="h-px bg-neutral-100 my-1" />
          <button
            onClick={handleLogout}
            className="flex items-center gap-2.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors w-full text-left"
          >
            <LogOut className="w-4 h-4" />
            {t("userMenu.logout")}
          </button>
        </div>
      )}
    </div>
  );
}
